import { useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "@charisol-spark/ui";
import { captureFirstTouch } from "@charisol-spark/client-core";
import { ErrorBoundary } from "./ErrorBoundary";
import { SiteRouter } from "./routes";
import { SessionProvider, type SiteUser } from "./session";
import "./website.css";

interface SiteAppProps {
  /**
   * The signed-in user, or null. Read by the host entry and passed in, so this
   * package never imports the app's auth store — see session.tsx.
   */
  user: SiteUser | null;
}

/**
 * Root of the marketing site.
 *
 * Deliberately thinner than the app's: no QueryClientProvider, no toaster, no
 * Temporal polyfill. None of these pages read server state, and leaving those
 * out is most of why this bundle is a fraction of the app's.
 */
export function SiteApp({ user }: SiteAppProps) {
  // As early as this component can run, on every page of the marketing
  // site — mirrors the frontend app's own entry. captureFirstTouch is a
  // no-op once the cookie is already set, so this never overwrites a
  // visitor's true first touch on a later visit or a route change.
  useEffect(() => {
    captureFirstTouch();
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider defaultTheme="system" storageKey="spark-theme">
          <SessionProvider user={user}>
            <SiteRouter />
          </SessionProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
